import { X, Keyboard } from "lucide-react";

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ShortcutCategory {
  title: string;
  shortcuts: {
    keys: string[];
    description: string;
  }[];
}

const SHORTCUT_GROUPS: ShortcutCategory[] = [
  {
    title: "Navegación y Vistas (Teclas individuales)",
    shortcuts: [
      { keys: ["C"], description: "Redactar nuevo mensaje (sin Cmd / Ctrl)" },
      { keys: ["J", "↓"], description: "Siguiente correo en la lista" },
      { keys: ["K", "↑"], description: "Correo anterior en la lista" },
      { keys: ["Enter"], description: "Abrir / Seleccionar correo enfocado" },
      { keys: ["Esc"], description: "Cerrar visor o ventana modal" },
      { keys: ["/"], description: "Buscar en los correos" },
      { keys: ["?"], description: "Abrir esta guía de atajos" },
    ],
  },
  {
    title: "Acciones de Correo",
    shortcuts: [
      { keys: ["R"], description: "Responder al remitente" },
      { keys: ["Shift", "R"], description: "Responder a todos" },
      { keys: ["F"], description: "Reenviar correo actual" },
      { keys: ["E", "Del"], description: "Mover correo a la Papelera" },
      { keys: ["S"], description: "Marcar / Desmarcar como destacado (Estrella)" },
      { keys: ["U"], description: "Alternar leído / no leído" },
      { keys: ["X"], description: "Seleccionar casilla del correo actual" },
      { keys: ["Shift", "A"], description: "Seleccionar todos los correos" },
    ],
  },
  {
    title: "Redacción y Atajos del Sistema",
    shortcuts: [
      { keys: ["Ctrl", "Enter"], description: "Enviar correo" },
      { keys: ["⌘", "Enter"], description: "Enviar correo (macOS)" },
      { keys: ["Ctrl / ⌘", "S"], description: "Guardar borrador manualmente" },
      { keys: ["Ctrl / ⌘", "C"], description: "Copiar texto seleccionado (Sistema)" },
      { keys: ["Ctrl / ⌘", "V"], description: "Pegar texto (Sistema)" },
    ],
  },
  {
    title: "Gestos Táctiles (Móvil / Tablet)",
    shortcuts: [
      { keys: ["Deslizar →"], description: "Deslizar a la derecha: Marcar leído / no leído" },
      { keys: ["Deslizar ←"], description: "Deslizar a la izquierda: Mover a Papelera" },
    ],
  },
];

export default function KeyboardShortcutsModal({
  isOpen,
  onClose,
}: KeyboardShortcutsModalProps) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-[var(--bg-primary)] border border-[var(--border-color)] shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--text-primary)] flex items-center gap-2">
            <Keyboard size={14} className="text-accent" />
            Atajos de Teclado
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
            title="Cerrar (Esc)"
          >
            <X size={15} />
          </button>
        </div>

        {/* Shortcuts list */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {SHORTCUT_GROUPS.map((group, idx) => (
            <div key={idx} className="space-y-2">
              <h4 className="text-2xs font-bold uppercase tracking-wider text-[var(--text-muted)]">
                {group.title}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {group.shortcuts.map((shortcut, sIdx) => (
                  <div
                    key={sIdx}
                    className="flex items-center justify-between p-2 bg-[var(--bg-secondary)] text-xs"
                  >
                    <span className="text-xs text-[var(--text-primary)] pr-2">
                      {shortcut.description}
                    </span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {shortcut.keys.map((key, kIdx) => (
                        <kbd
                          key={kIdx}
                          className="px-1.5 py-0.5 text-2xs font-mono font-semibold bg-[var(--bg-primary)] text-[var(--text-primary)] border border-[var(--border-subtle)] shadow-2xs min-w-[20px] text-center"
                        >
                          {key}
                        </kbd>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-2.5 border-t border-[var(--border-subtle)] bg-[var(--bg-secondary)] flex justify-end">
          <button
            onClick={onClose}
            className="px-3.5 py-1 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
